/* ============================================================
   PORTICO STUDIOS — THEME JS
   ============================================================ */

(function () {
  'use strict';

  // ── Lenis smooth scroll ────────────────────────────────────
  let lenis;
  if (typeof Lenis !== 'undefined' && window.innerWidth > 768) {
    lenis = new Lenis({
      lerp: 0.08,
      smoothWheel: true,
      syncTouch: false,
      syncTouchLerp: 0.06,
      touchInertiaExponent: 1.7,
    });
  }

  // ── Canvas background (lava-lamp animated, skylrk-style) ──
  const bgCanvas = document.getElementById('bgCanvas');
  const bgCtx    = bgCanvas ? bgCanvas.getContext('2d', { alpha: false }) : null;
  let   resetTimer = null;

  // h° is irrelevant when s=0. Idle = pure white with cloud-depth from blobs.
  // Hover = hue + saturation bloom over the same cloud forms.
  const col = { h:0, s:0, l:0.96 };   // animated current
  const tgt = { h:0, s:0, l:0.96 };   // lerp target

  // Accent overlay — keeps the field neutral white/grey but tints a few blobs
  // a strong color (navy "hits"). `on` (0–1) fades the accent in/out smoothly.
  // Triggered by passing an "accent:#HEX" value to applyBackgroundFromHex.
  // Pass "xray" for the X-RAY THERMAL black/white/blue film look.
  const acc    = { h:225, s:0.6, l:0.30, on:0 };
  const accTgt = { h:225, s:0.6, l:0.30, on:0 };
  const ACCENT_BLOB_IDX = [0, 4, 6];  // which cloud blobs get the accent tint

  // Product scene — the hover/scroll background. A mid-tone field with a big
  // white light and a big dark shade that wander the whole screen on slow
  // looping paths, under heavy grain (studio-light look). `on` fades it over
  // the idle white cloud. Each layer is { h, s, l, a } and lerps between products.
  // Layers are stored as RGB so transitions blend straight from one product's
  // colors to the next (HSL lerping swung through the hue wheel — red→navy
  // went via purple). setScene still takes { h, s, l, a } for convenience.
  const scene    = { on:0, f:{r:245,g:245,b:245,a:1}, lt:{r:255,g:255,b:255,a:0.9}, sh:{r:26,g:26,b:26,a:0.9} };
  const sceneTgt = { on:0, f:{r:245,g:245,b:245,a:1}, lt:{r:255,g:255,b:255,a:0.9}, sh:{r:26,g:26,b:26,a:0.9} };

  function hslToRgb(h, s, l) {
    h = ((h % 360) + 360) % 360 / 360;
    if (s === 0) { const v = l * 255; return { r:v, g:v, b:v }; }
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s, p = 2 * l - q;
    const f = t => { t = (t + 1) % 1;
      return t < 1/6 ? p + (q - p) * 6 * t : t < 1/2 ? q : t < 2/3 ? p + (q - p) * (2/3 - t) * 6 : p; };
    return { r: f(h + 1/3) * 255, g: f(h) * 255, b: f(h - 1/3) * 255 };
  }
  let sceneT = Math.random() * 100;   // random start so every visit flows differently

  function setScene(f, lt, sh) {
    [['f', f], ['lt', lt], ['sh', sh]].forEach(([k, c]) => {
      Object.assign(sceneTgt[k], hslToRgb(c.h, Math.min(c.s, 1), Math.min(Math.max(c.l, 0), 1)), { a: c.a });
    });
    sceneTgt.on = 1;
  }

  // Derive field / light / shade from one product color.
  function scenePalette(h, s, l) {
    const clamp = (v, a, b) => Math.min(Math.max(v, a), b);
    if (s < 0.12) {                       // neutrals: black / grey / white
      if (l < 0.35) return setScene({h, s:0.02, l:0.30, a:1}, {h, s:0, l:0.92, a:0.85}, {h, s:0.02, l:0.04, a:0.95});
      if (l < 0.75) return setScene({h, s:0.03, l:0.58, a:1}, {h, s:0, l:0.97, a:0.85}, {h, s:0.03, l:0.18, a:0.85});
      return          setScene({h, s:0.04, l:0.86, a:1}, {h, s:0, l:1.00, a:0.90}, {h, s:0.04, l:0.55, a:0.70});
    }
    const fl = l < 0.30 ? 0.34 : l > 0.85 ? 0.88 : clamp(l + 0.18, 0.50, 0.72);
    const fs = clamp(s, 0.35, 0.62);
    setScene(
      {h, s:fs, l:fl, a:1},
      {h, s:0.30, l: l < 0.30 ? 0.86 : 0.97, a:0.9},
      {h, s:clamp(s * 1.1, 0.45, 0.75), l: l > 0.85 ? 0.60 : l < 0.30 ? 0.06 : 0.15, a:0.92}
    );
  }

  function hexToHsl(hex) {
    const c = hex.replace('#','');
    const r = parseInt(c.slice(0,2),16)/255;
    const g = parseInt(c.slice(2,4),16)/255;
    const b = parseInt(c.slice(4,6),16)/255;
    const max=Math.max(r,g,b), min=Math.min(r,g,b);
    const l=(max+min)/2;
    if (max===min) return [0,0,l];
    const d=max-min;
    const s=l>0.5 ? d/(2-max-min) : d/(max+min);
    let h = max===r ? ((g-b)/d+(g<b?6:0))/6
           : max===g ? ((b-r)/d+2)/6
           :            ((r-g)/d+4)/6;
    return [h*360, s, l];
  }

  function setTargetHex(hex) {
    if (!hex) { tgt.h=0; tgt.s=0; tgt.l=0.96; accTgt.on=0; sceneTgt.on=0; return; }

    // Custom scene: "scene:#FIELD,#LIGHT,#SHADE" — exact colors per product
    if (hex.indexOf('scene:') === 0) {
      const [fh, lh, sh] = hex.slice(6).split(',').map(x => hexToHsl(x.trim()));
      const [h, s, l] = fh;
      tgt.h = h; tgt.s = s; tgt.l = l; accTgt.on = 0;
      setScene({h:fh[0], s:fh[1], l:fh[2], a:1}, {h:lh[0], s:lh[1], l:lh[2], a:0.9}, {h:sh[0], s:sh[1], l:sh[2], a:0.9});
      return;
    }

    // X-ray mode (X-RAY THERMAL): black film field, white light, X-ray-blue sweep
    if (hex === 'xray') {
      tgt.h = 212; tgt.s = 0.03; tgt.l = 0.06; accTgt.on = 0;
      setScene({h:212, s:0.06, l:0.06, a:1}, {h:200, s:0.20, l:0.93, a:0.80}, {h:200, s:0.60, l:0.26, a:0.55});
      return;
    }

    // Accent mode: "accent:#HEX" → neutral white/grey field with colored blob hits
    if (hex.indexOf('accent:') === 0) {
      const [h,s,l] = hexToHsl(hex.slice(7));
      // Field stays light/neutral with only the faintest cool cast
      tgt.h = h; tgt.s = 0.05; tgt.l = 0.93;
      // The "hits" carry the real color, kept rich and moody
      accTgt.h = h;
      accTgt.s = Math.min(s * 1.3, 0.85);
      accTgt.l = Math.min(Math.max(l, 0.22), 0.40);
      accTgt.on = 1;
      // Scene: pale cool field, white light, deep shade of the accent color
      setScene({h, s:0.55, l:0.74, a:1}, {h, s:0.25, l:0.98, a:0.9}, {h, s:Math.min(Math.max(s, 0.5), 0.75), l:0.16, a:0.92});
      return;
    }

    const [h,s,l] = hexToHsl(hex);
    tgt.h = h;
    tgt.s = Math.min(s * 1.4, 0.72);  // no forced min — black stays s≈0, dark stays dark
    // Respect the product's actual darkness: black→dark bg, tan→light warm bg
    tgt.l = l < 0.30
      ? l * 1.3 + 0.14          // dark products: bg 0.14–0.53, moody
      : Math.min(l + 0.14, 0.93); // light products: nudge bright
    accTgt.on = 0;
    scenePalette(h, s, l);
  }

  function lerpAngle(a, b, t) {
    let d = ((b - a + 540) % 360) - 180;
    return a + d * t;
  }
  function lerp(a,b,t){ return a+(b-a)*t; }

  function resizeBg() {
    if (!bgCanvas) return;
    bgCanvas.width  = window.innerWidth;
    bgCanvas.height = window.innerHeight;
    bgSz = Math.min(window.innerWidth, window.innerHeight);
  }
  window.addEventListener('resize', resizeBg);

  // Pre-generate 8 grain frames at startup — cycling them costs zero CPU per frame.
  const GRAIN_FRAMES = Array.from({ length: 8 }, () => {
    const gc = document.createElement('canvas');
    gc.width = gc.height = 256;
    const gctx = gc.getContext('2d');
    const id = gctx.createImageData(256, 256);
    const d = id.data;
    for (let i = 0; i < d.length; i += 4) {
      const v = Math.random() * 255 | 0;
      d[i] = d[i+1] = d[i+2] = v; d[i+3] = 255;
    }
    gctx.putImageData(id, 0, 0);
    return gc;
  });
  let grainFrameIdx = 0;
  let grainTick = 0;

  let bgT = 0;
  let bgSz = Math.min(window.innerWidth, window.innerHeight);

  // Cloud blobs — large, slow, heavily overlapping for a soft cumulus look.
  // lBase is absolute lightness at idle; depth comes from offset vs IDLE_L_AVG (0.88).
  const BLOBS = [
    // Primary cloud masses — very large, slow drift
    { ox:0.18, oy:0.28, sx:0.32, sy:0.24, sp:0.07, ph:0.00, rs:1.45, oA:0.52, lBase:0.96 },
    { ox:0.80, oy:0.58, sx:0.26, sy:0.28, sp:0.06, ph:1.80, rs:1.38, oA:0.48, lBase:0.97 },
    { ox:0.50, oy:0.75, sx:0.28, sy:0.20, sp:0.05, ph:3.50, rs:1.28, oA:0.46, lBase:0.92 },
    // Mid-tone cloud bodies
    { ox:0.32, oy:0.12, sx:0.24, sy:0.26, sp:0.08, ph:2.20, rs:1.14, oA:0.44, lBase:0.87 },
    { ox:0.68, oy:0.84, sx:0.30, sy:0.22, sp:0.06, ph:4.60, rs:1.18, oA:0.42, lBase:0.89 },
    // Shadow/depth pockets — slightly smaller, add tonal variation
    { ox:0.60, oy:0.32, sx:0.22, sy:0.24, sp:0.09, ph:1.10, rs:1.00, oA:0.40, lBase:0.78 },
    { ox:0.24, oy:0.68, sx:0.26, sy:0.20, sp:0.07, ph:5.80, rs:1.06, oA:0.38, lBase:0.82 },
    { ox:0.44, oy:0.46, sx:0.20, sy:0.24, sp:0.10, ph:3.80, rs:0.94, oA:0.35, lBase:0.72 },
  ];

  const onMobile = window.innerWidth <= 768;

  function drawBg() {
    if (!bgCtx) return;
    const W = bgCanvas.width, H = bgCanvas.height;

    // Mobile: faster transitions so color keeps up with snap scrolling
    const spd = onMobile
      ? (col.s < tgt.s ? 0.22 : 0.08)
      : (col.s < tgt.s ? 0.10 : 0.038);
    col.h = lerpAngle(col.h, tgt.h, spd);
    col.s = lerp(col.s, tgt.s, spd);
    col.l = lerp(col.l, tgt.l, spd);

    // Base fill tracks the overall mood
    const baseS = Math.round(col.s * 42);
    const baseL = Math.round(col.l * 100);
    bgCtx.fillStyle = `hsl(${Math.round(col.h)},${baseS}%,${baseL}%)`;
    bgCtx.fillRect(0, 0, W, H);

    const IDLE_L_AVG = 0.88;
    const colH = Math.round(col.h);
    const colS = Math.min(Math.round(col.s * 185), 72);

    BLOBS.forEach((b, i) => {
      // Dual sine for organic, non-circular drift
      const cx = (b.ox + Math.sin(bgT * b.sp + b.ph) * b.sx + Math.sin(bgT * b.sp * 0.41 + b.ph * 1.7) * b.sx * 0.30) * W;
      const cy = (b.oy + Math.cos(bgT * b.sp * 0.73 + b.ph) * b.sy + Math.cos(bgT * b.sp * 0.29 + b.ph * 2.1) * b.sy * 0.30) * H;

      const bH = (colH + i * 12) % 360;
      const bL = Math.round(Math.max(0.03, Math.min(0.97, col.l + b.lBase - IDLE_L_AVG)) * 100);
      const oA = b.oA;

      const rs = b.rs * bgSz;
      const g = bgCtx.createRadialGradient(cx, cy, 0, cx, cy, rs);
      g.addColorStop(0,    `hsla(${bH},${colS}%,${bL}%,${oA})`);
      g.addColorStop(0.30, `hsla(${bH},${colS}%,${bL}%,${oA * 0.75})`);
      g.addColorStop(0.58, `hsla(${bH},${colS}%,${bL}%,${oA * 0.26})`);
      g.addColorStop(0.82, `hsla(${bH},${colS}%,${bL}%,${oA * 0.06})`);
      g.addColorStop(1,    `hsla(${bH},${colS}%,${bL}%,0)`);

      bgCtx.fillStyle = g;
      bgCtx.fillRect(0, 0, W, H);
    });

    // Accent "hits" — a few blobs tinted with the accent color over the neutral
    // field. acc.on fades the whole effect in/out so it animates smoothly.
    acc.h  = lerpAngle(acc.h, accTgt.h, spd);
    acc.s  = lerp(acc.s,  accTgt.s,  spd);
    acc.l  = lerp(acc.l,  accTgt.l,  spd);
    acc.on = lerp(acc.on, accTgt.on, spd);
    if (acc.on > 0.01) {
      const aH = Math.round(acc.h);
      const aS = Math.min(Math.round(acc.s * 100), 90);
      const aL = Math.round(acc.l * 100);
      const peak = 0.52 * acc.on;
      ACCENT_BLOB_IDX.forEach(idx => {
        const b = BLOBS[idx];
        const cx = (b.ox + Math.sin(bgT * b.sp + b.ph) * b.sx + Math.sin(bgT * b.sp * 0.41 + b.ph * 1.7) * b.sx * 0.30) * W;
        const cy = (b.oy + Math.cos(bgT * b.sp * 0.73 + b.ph) * b.sy + Math.cos(bgT * b.sp * 0.29 + b.ph * 2.1) * b.sy * 0.30) * H;
        const rs = b.rs * bgSz * 0.82;
        const g = bgCtx.createRadialGradient(cx, cy, 0, cx, cy, rs);
        g.addColorStop(0,    `hsla(${aH},${aS}%,${aL}%,${peak})`);
        g.addColorStop(0.35, `hsla(${aH},${aS}%,${aL}%,${peak * 0.6})`);
        g.addColorStop(0.70, `hsla(${aH},${aS}%,${aL}%,${peak * 0.15})`);
        g.addColorStop(1,    `hsla(${aH},${aS}%,${aL}%,0)`);
        bgCtx.fillStyle = g;
        bgCtx.fillRect(0, 0, W, H);
      });
    }

    // Product scene — field + wandering light + wandering shade
    const sUp = sceneTgt.on > scene.on;
    const onSpd  = onMobile ? (sUp ? 0.10 : 0.06) : (sUp ? 0.06 : 0.035);  // fade in / out
    // Color blend between products. Snap colors while the scene is invisible
    // so a fresh hover fades in the right palette instead of the old one.
    const colSpd = scene.on < 0.02 ? 1 : (onMobile ? 0.07 : 0.05);
    scene.on = lerp(scene.on, sceneTgt.on, onSpd);
    ['f', 'lt', 'sh'].forEach(k => {
      const c = scene[k], t = sceneTgt[k];
      c.r = lerp(c.r, t.r, colSpd); c.g = lerp(c.g, t.g, colSpd);
      c.b = lerp(c.b, t.b, colSpd); c.a = lerp(c.a, t.a, colSpd);
    });
    if (scene.on > 0.01) {
      const M = Math.max(W, H);
      const hsla = (c, a) => `rgba(${Math.round(c.r)},${Math.round(c.g)},${Math.round(c.b)},${a})`;
      // Soft faded shape — color fades continuously from the middle out (no
      // solid core), so it reads as a haze of color, not a flat circle.
      const blob = (c, x, y, r, stretch, rot, a) => {
        bgCtx.save();
        bgCtx.translate(x, y);
        bgCtx.rotate(rot);
        bgCtx.scale(1, stretch);
        const g = bgCtx.createRadialGradient(0, 0, 0, 0, 0, r);
        g.addColorStop(0,    hsla(c, a * 0.82));
        g.addColorStop(0.22, hsla(c, a * 0.66));
        g.addColorStop(0.48, hsla(c, a * 0.40));
        g.addColorStop(0.74, hsla(c, a * 0.14));
        g.addColorStop(1,    hsla(c, 0));
        bgCtx.fillStyle = g;
        bgCtx.fillRect(-3 * M, -3 * M, 6 * M, 6 * M);
        bgCtx.restore();
      };
      bgCtx.save();
      bgCtx.globalAlpha = scene.on;
      bgCtx.fillStyle = hsla(scene.f, 1);
      bgCtx.fillRect(0, 0, W, H);
      const t = sceneT;
      // Shade — deep, solid column that roams the middle of the screen
      blob(scene.sh,
        (0.50 + 0.36 * Math.sin(t * 0.61 + 2.4)) * W,
        (0.58 + 0.34 * Math.cos(t * 0.83 + 1.1)) * H,
        M * 0.58, 1.55, 0.25 * Math.sin(t * 0.23), scene.sh.a);
      // Light — a broad band whose core stays mostly off-screen, so it sweeps
      // in from the edges instead of sitting on screen like a sun.
      blob(scene.lt,
        (0.50 + 0.80 * Math.sin(t * 0.47 + 5.6)) * W,
        (0.50 + 0.45 * Math.cos(t * 0.71 + 4.2)) * H,
        M * 0.50, 1.90, -0.2 + 0.25 * Math.cos(t * 0.19), scene.lt.a);
      bgCtx.restore();
      sceneT += onMobile ? 0.0022 : 0.0026;
    }

    // Cycle pre-generated grain frames — zero CPU cost per frame
    const grainCanvas = GRAIN_FRAMES[grainFrameIdx % GRAIN_FRAMES.length];
    grainTick++;
    if (grainTick % 6 === 0) grainFrameIdx++;
    bgCtx.save();
    bgCtx.globalAlpha = 0.11 - 0.07 * scene.on;   // lighter grain under product scenes
    bgCtx.globalCompositeOperation = 'multiply';
    for (let x = 0; x < W; x += 256) {
      for (let y = 0; y < H; y += 256) {
        bgCtx.drawImage(grainCanvas, x, y);
      }
    }
    bgCtx.restore();
    if (scene.on > 0.01) {
      bgCtx.save();
      bgCtx.globalAlpha = 0.06 * scene.on;
      bgCtx.globalCompositeOperation = 'overlay';
      const g2 = GRAIN_FRAMES[(grainFrameIdx + 3) % GRAIN_FRAMES.length];
      for (let x = 0; x < W; x += 256) {
        for (let y = 0; y < H; y += 256) {
          bgCtx.drawImage(g2, x, y);
        }
      }
      bgCtx.restore();
    }

    bgT += 0.004;

    // Flip header pill to light-on-dark when background is dark
    const fieldL = (0.2126 * scene.f.r + 0.7152 * scene.f.g + 0.0722 * scene.f.b) / 255;
    const isDark = (scene.on > 0.5 ? fieldL : col.l) < 0.48;
    if (document.body.classList.contains('bg-dark') !== isDark) {
      document.body.classList.toggle('bg-dark', isDark);
    }
  }

  function applyBackgroundFromHex(hex) {
    setTargetHex(hex || null);
    clearTimeout(resetTimer);
  }

  function resetBackground(delay) {
    clearTimeout(resetTimer);
    resetTimer = setTimeout(() => setTargetHex(null), delay || 600);
  }

  function cancelReset() { clearTimeout(resetTimer); }

  // Expose globally so product-hero inline script can reach them
  window.applyBackgroundFromHex = applyBackgroundFromHex;
  window.resetBackground = resetBackground;

  // startAmbientDrift is a no-op now — the rAF loop handles it
  function startAmbientDrift() {}

  // ── Color extraction from image pixels ────────────────────
  function extractColor(img, callback) {
    try {
      const canvas = document.createElement('canvas');
      const ctx    = canvas.getContext('2d');
      const size   = 80;
      canvas.width = canvas.height = size;
      ctx.drawImage(img, 0, 0, size, size);
      const data = ctx.getImageData(0, 0, size, size).data;

      let r=0, g=0, b=0, count=0;
      for (let i=0; i<data.length; i+=4) {
        if (data[i+3] < 120) continue;
        const ri=data[i], gi=data[i+1], bi=data[i+2];
        const br = (ri+gi+bi)/3;
        if (br > 235 || br < 20) continue;
        r+=ri; g+=gi; b+=bi; count++;
      }

      if (count > 80) {
        callback('#' + [r,g,b].map(v => Math.round(v/count).toString(16).padStart(2,'0')).join(''));
        return;
      }
    } catch(e) {}
    callback(null);
  }

  // ── Motion system — always starts (drives drawBg + lenis), scatter motion optional ──
  function initMotion() {
    const items   = Array.from(document.querySelectorAll('.scatter-item'));
    const isMobile = window.innerWidth < 768;

    // Scatter-specific state — only allocated when items exist
    const floatParams = items.map((_, i) => ({
      speed:   0.28 + (i * 0.09) % 0.28,
      amplitude: 0,
      rotAmp:  0.15 + (i * 0.08) % 0.2,
      phase:   (i * 2.39996) % (Math.PI * 2),
    }));
    const tilt = items.map(() => ({ tx:0, ty:0, targetTx:0, targetTy:0, scale:1, targetScale:1 }));

    function lerpLocal(a, b, t) { return a + (b - a) * t; }

    let mouseX = window.innerWidth / 2, mouseY = window.innerHeight / 2;
    let targetMouseX = mouseX, targetMouseY = mouseY;

    if (items.length && !isMobile) {
      document.addEventListener('mousemove', e => {
        targetMouseX = e.clientX;
        targetMouseY = e.clientY;
      });
      items.forEach((item, i) => {
        item.addEventListener('mouseenter', () => { tilt[i].targetScale = 1.07; });
        item.addEventListener('mousemove', e => {
          const rect = item.getBoundingClientRect();
          const dx = (e.clientX - (rect.left + rect.width  / 2)) / (rect.width  / 2);
          const dy = (e.clientY - (rect.top  + rect.height / 2)) / (rect.height / 2);
          tilt[i].targetTx = dy * -6;
          tilt[i].targetTy = dx *  6;
        });
        item.addEventListener('mouseleave', () => {
          tilt[i].targetScale = 1;
          tilt[i].targetTx = 0;
          tilt[i].targetTy = 0;
        });
      });
    }

    let startTime = null;

    function tick(ts) {
      if (!startTime) startTime = ts;
      const t = (ts - startTime) / 1000;

      drawBg();                       // always — works on every page
      if (lenis) lenis.raf(ts);       // always

      if (!isMobile) {
        mouseX = lerpLocal(mouseX, targetMouseX, 0.055);
        mouseY = lerpLocal(mouseY, targetMouseY, 0.055);
      }

      if (items.length && !isMobile) {
        const cx  = window.innerWidth  / 2;
        const cy  = window.innerHeight / 2;
        const mdx = (mouseX - cx) / cx;
        const mdy = (mouseY - cy) / cy;

        items.forEach((item, i) => {
          const p      = floatParams[i];
          const floatY = Math.sin(t * p.speed + p.phase) * p.amplitude;
          const floatR = Math.sin(t * p.speed * 0.65 + p.phase + 0.8) * p.rotAmp;
          const px     = mdx * window.innerWidth  * (0.011 + (i % 3) * 0.005);
          const py     = mdy * window.innerHeight * (0.011 + (i % 3) * 0.005);
          const ti     = tilt[i];

          ti.tx    = lerpLocal(ti.tx,    ti.targetTx,    0.1);
          ti.ty    = lerpLocal(ti.ty,    ti.targetTy,    0.1);
          ti.scale = lerpLocal(ti.scale, ti.targetScale, 0.1);

          const perspStr = ti.scale > 1.001
            ? ` perspective(600px) rotateX(${ti.tx.toFixed(2)}deg) rotateY(${ti.ty.toFixed(2)}deg)`
            : '';

          item.style.transform =
            `translate(${px.toFixed(2)}px,${(py + floatY).toFixed(2)}px)` +
            ` rotate(${floatR.toFixed(3)}deg)` +
            ` scale(${ti.scale.toFixed(3)})` +
            perspStr;
        });
      }

      requestAnimationFrame(tick);
    }

    requestAnimationFrame(tick);
  }

  // ── Hover color (desktop) ──────────────────────────────────
  function initScatterHover() {
    const items = document.querySelectorAll('.scatter-item');

    items.forEach(item => {
      const img = item.querySelector('img');
      if (!img) return;

      function setupHover(extractedColor) {
        const color = item.dataset.bg || extractedColor || null;
        item.addEventListener('mouseenter', () => {
          cancelReset();
          if (color) applyBackgroundFromHex(color);
        });
        item.addEventListener('mouseleave', () => resetBackground(700));
      }

      if (img.complete && img.naturalWidth) {
        extractColor(img, setupHover);
      } else {
        img.addEventListener('load', () => extractColor(img, setupHover));
        img.addEventListener('error', () => setupHover(null));
      }
    });
  }

  // ── Mobile scroll color ────────────────────────────────────
  function initScrollColor() {
    if (window.innerWidth > 768) return;
    const section = document.querySelector('.scatter-section');
    const canvas  = document.getElementById('scatterCanvas');
    if (!section || !canvas) return;

    let lastColor = null;

    function applyColorForIndex(idx) {
      const items = canvas.querySelectorAll('.scatter-item');
      const item  = items[idx];
      if (!item) return;
      const color = item.dataset.bg || null;
      if (color !== lastColor) {
        lastColor = color;
        if (color) applyBackgroundFromHex(color);
        else applyBackgroundFromHex(null);
      }
    }

    function updateColor() {
      const ph = section.clientHeight;
      if (!ph) return;
      applyColorForIndex(Math.round(section.scrollTop / ph));
    }

    // Fire on every scroll so color tracks the swipe in real time
    section.addEventListener('scroll', updateColor, { passive: true });

    // Apply initial color after initMobileSnap has set scroll position (two frames out)
    requestAnimationFrame(() => requestAnimationFrame(() => updateColor()));
  }

  // ── Mobile infinite snap loop ──────────────────────────────
  function initMobileSnap() {
    if (window.innerWidth > 768) return;
    const section = document.querySelector('.scatter-section');
    const canvas  = document.getElementById('scatterCanvas');
    if (!section || !canvas) return;

    const origItems = Array.from(canvas.querySelectorAll('.scatter-item'));
    if (origItems.length < 2) return;

    // Layout: [cloneOfLast] [item0..itemN-1] [cloneOfFirst]
    const startClone = origItems[origItems.length - 1].cloneNode(true);
    const endClone   = origItems[0].cloneNode(true);
    startClone.setAttribute('aria-hidden', 'true');
    endClone.setAttribute('aria-hidden', 'true');
    canvas.insertBefore(startClone, origItems[0]);
    canvas.appendChild(endClone);

    // Defer so layout is computed; scrollTo({ behavior:'instant' }) skips snap animation
    requestAnimationFrame(() => {
      section.scrollTo({ top: section.clientHeight, behavior: 'instant' });
    });

    let jumping = false;

    const checkWrap = () => {
      if (jumping) return;
      const top = section.scrollTop;
      const ph  = section.clientHeight;
      const n   = origItems.length;

      let dest = -1;
      if (top < ph * 0.5)       dest = ph * n;  // startClone → jump to real last
      if (top > ph * (n + 0.5)) dest = ph;       // endClone   → jump to real first
      if (dest < 0) return;

      jumping = true;
      section.scrollTo({ top: dest, behavior: 'instant' });
      setTimeout(() => { jumping = false; }, 80);
    };

    if ('onscrollend' in window) {
      section.addEventListener('scrollend', checkWrap, { passive: true });
    } else {
      let t;
      section.addEventListener('scroll', () => {
        clearTimeout(t); t = setTimeout(checkWrap, 200);
      }, { passive: true });
    }
  }

  // ── Cart ───────────────────────────────────────────────────
  const cartOverlay = document.getElementById('cartOverlay');
  const cartDrawer  = document.getElementById('cartDrawer');

  function openCart() {
    if (!cartDrawer) return;
    cartDrawer.classList.add('open');
    cartOverlay?.classList.add('open');
    document.body.classList.add('no-scroll');
    refreshCart();
  }

  function closeCart() {
    if (!cartDrawer) return;
    cartDrawer.classList.remove('open');
    cartOverlay?.classList.remove('open');
    document.body.classList.remove('no-scroll');
  }

  // Expose globally so product page inline scripts can call them
  window.openCart    = openCart;
  window.refreshCart = () => refreshCart();

  document.addEventListener('click', e => {
    if (e.target.closest('[data-cart-trigger]')) { e.preventDefault(); openCart(); }
    if (e.target.closest('[data-cart-close]') || e.target === cartOverlay) closeCart();
  });

  // Remove item from cart
  document.addEventListener('click', async e => {
    const btn = e.target.closest('.cart-remove');
    if (!btn) return;
    btn.disabled = true;
    btn.textContent = '…';
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: parseInt(btn.dataset.line), quantity: 0 })
      });
      if (res.ok) refreshCart();
    } catch (_) { btn.disabled = false; btn.textContent = '✕'; }
  });

  function formatMoney(cents) { return '$' + (cents / 100).toFixed(2); }

  async function refreshCart() {
    try {
      const res  = await fetch('/cart.js');
      const cart = await res.json();
      updateCartBadge(cart.item_count);
      renderCartDrawer(cart);
    } catch (_) {}
  }

  function updateCartBadge(count) {
    document.querySelectorAll('.cart-badge').forEach(b => {
      b.textContent = count;
      b.classList.toggle('visible', count > 0);
    });
  }

  function renderCartDrawer(cart) {
    const itemsEl    = document.getElementById('cartItems');
    const subtotalEl = document.getElementById('cartSubtotal');
    if (!itemsEl) return;

    if (!cart.item_count) {
      itemsEl.innerHTML = '<p class="cart-empty-msg">Your cart is empty</p>';
    } else {
      itemsEl.innerHTML = cart.items.map((item, idx) => {
        const hasVariant = item.variant_title && item.variant_title !== 'Default Title';
        const qty        = item.quantity > 1 ? `<span class="cart-item-qty">× ${item.quantity}</span>` : '';
        return `
        <div class="cart-item">
          <div class="cart-item-img">
            <img src="${item.image}" alt="${item.product_title}" loading="lazy">
          </div>
          <div class="cart-item-info">
            <div class="cart-item-name">${item.product_title}</div>
            ${hasVariant ? `<div class="cart-item-variant">[ ${item.variant_title.toUpperCase()} ]</div>` : ''}
            <div class="cart-item-bottom">
              <span class="cart-item-price">${formatMoney(item.final_price)}</span>
              ${qty}
              <button class="cart-remove" data-line="${idx + 1}" aria-label="Remove">✕</button>
            </div>
          </div>
        </div>`;
      }).join('');
    }

    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);
  }

  // ── Product form ───────────────────────────────────────────
  function initProductForm() {
    const form = document.getElementById('productForm');
    if (!form) return;

    form.addEventListener('submit', async e => {
      e.preventDefault();
      const btn          = form.querySelector('.atc-btn');
      const variantInput = form.querySelector('[name="id"]');
      if (!variantInput?.value) return;

      btn.disabled = true;
      btn.innerHTML = '<span>ADDING…</span><span></span>';

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: parseInt(variantInput.value), quantity: 1 })
        });

        if (res.ok) {
          btn.innerHTML = '<span>ADDED ✓</span><span></span>';
          setTimeout(() => { btn.disabled = false; restoreAtcBtn(btn); openCart(); }, 1100);
        } else {
          btn.innerHTML = '<span>SOLD OUT</span><span></span>';
          setTimeout(() => { btn.disabled = false; restoreAtcBtn(btn); }, 2200);
        }
      } catch (_) {
        btn.innerHTML = '<span>TRY AGAIN</span><span></span>';
        btn.disabled = false;
      }
    });
  }

  function restoreAtcBtn(btn) {
    const priceEl = document.getElementById('variantPrice');
    const price   = priceEl ? priceEl.textContent : '';
    btn.innerHTML = `<span>ADD TO CART</span><span>${price}</span>`;
  }

  // ── Product selectors ──────────────────────────────────────
  function initProductSelectors() {
    const variants     = window.productVariants || [];
    const variantInput = document.querySelector('[name="id"]');

    document.querySelectorAll('.swatch').forEach(sw => {
      sw.addEventListener('click', () => {
        document.querySelectorAll('.swatch').forEach(s => s.classList.remove('active'));
        sw.classList.add('active');
        const valEl = document.getElementById('selectedColor');
        if (valEl) valEl.textContent = sw.dataset.colorName || '';
        if (sw.dataset.bg) applyBackgroundFromHex(sw.dataset.bg);
        matchVariant(variants, variantInput);
      });
    });

    document.querySelectorAll('.size-btn:not(.unavailable)').forEach(btn => {
      btn.addEventListener('click', () => {
        document.querySelectorAll('.size-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        const valEl = document.getElementById('selectedSize');
        if (valEl) valEl.textContent = btn.dataset.size || '';
        matchVariant(variants, variantInput);
      });
    });
  }

  function matchVariant(variants, input) {
    const selectedColor = document.querySelector('.swatch.active')?.dataset.colorName;
    const selectedSize  = document.querySelector('.size-btn.active')?.dataset.size;

    const match = variants.find(v => {
      const opts = [v.option1, v.option2, v.option3].filter(Boolean).map(o => o.toLowerCase());
      return (!selectedColor || opts.includes(selectedColor.toLowerCase()))
          && (!selectedSize  || opts.includes(selectedSize.toLowerCase()));
    });

    if (!match || !input) return;
    input.value = match.id;

    const priceEl = document.getElementById('variantPrice');
    const price   = formatMoney(match.price);
    if (priceEl) priceEl.textContent = price;

    const atcBtn = document.querySelector('.atc-btn');
    if (atcBtn) {
      atcBtn.disabled = !match.available;
      atcBtn.innerHTML = match.available
        ? `<span>ADD TO CART</span><span>${price}</span>`
        : '<span>SOLD OUT</span><span>—</span>';
    }
  }

  // ── Gallery thumbnails ─────────────────────────────────────
  function initGallery() {
    const mainImg = document.getElementById('mainProductImg');
    const thumbs  = document.querySelectorAll('.thumb');
    if (!mainImg || !thumbs.length) return;

    mainImg.style.transition = 'opacity 0.2s ease, transform 0.2s ease';

    thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => {
        thumbs.forEach(t => t.classList.remove('active'));
        thumb.classList.add('active');
        mainImg.style.opacity   = '0';
        mainImg.style.transform = 'scale(0.95)';
        setTimeout(() => {
          mainImg.src             = thumb.dataset.src;
          mainImg.style.opacity   = '1';
          mainImg.style.transform = 'scale(1)';
        }, 180);
      });
    });
  }

  // ── Init ───────────────────────────────────────────────────
  document.addEventListener('DOMContentLoaded', () => {
    resizeBg();
    initMotion();
    initScatterHover();
    initMobileSnap();
    initScrollColor();
    initProductForm();
    initProductSelectors();
    initGallery();
    refreshCart();
    startAmbientDrift();
  });

})();
